/**
 * `ImageDetailResponse` → {@link ImageSeriesManifest}.
 *
 * Handwritten and hand-maintained, like `dataRetrieval.ts` next to it: the
 * generated client in this directory is regenerated wholesale and must never
 * be edited, so every mapping that carries meaning lives outside it.
 *
 * The DTO speaks DICOM wire conventions — backslash-joined multi-values,
 * every field optional-and-nullable because the generator says so. This module
 * is the one place that turns those into typed domain geometry, and it fails
 * fast on anything malformed rather than defaulting a clinical value into
 * existence: a silently invented pixel spacing or slice thickness would place
 * every contour drawn on the resulting volume in the wrong place.
 *
 * PHI: parse errors reach logs and toasts, so they name FIELDS and slice
 * INDICES, never SOP Instance UIDs or any other identifier.
 */

import { mat3, vec2, vec3 } from 'gl-matrix';
import { Modality, strToModality } from '~/domain/dicom/modality';
import {
    ImageSeriesManifest,
    ImageSeriesManifestSlice,
} from '~/domain/patient/imageSeriesManifest';
import { WindowLevel } from '~/domain/patient/windowLevel';
import type {
    ImageDetailResponse,
    ImageSliceResponse,
} from './connector-next-schemas';

/**
 * The image manifest could not be turned into usable geometry. Permanent, not
 * transient: retrying the same response produces the same failure.
 *
 * Carries a field name and, for per-slice problems, the slice's index in the
 * response — never a UID.
 */
export class ImageManifestParseError extends Error {
    public constructor(message: string) {
        super(message);
        this.name = 'ImageManifestParseError';
    }
}

/** DICOM multi-valued attributes arrive backslash-joined ("x\y\z"). */
const DICOM_VALUE_SEPARATOR = '\\';

function requireString(
    value: string | null | undefined,
    field: string
): string {
    if (value === null || value === undefined || value.trim() === '') {
        throw new ImageManifestParseError(`Image manifest is missing ${field}`);
    }
    return value.trim();
}

function requireNumber(
    value: number | null | undefined,
    field: string
): number {
    if (value === null || value === undefined || !Number.isFinite(value)) {
        throw new ImageManifestParseError(
            `Image manifest is missing a usable ${field}`
        );
    }
    return value;
}

/**
 * Parse a backslash-joined DICOM decimal string into exactly `expectedCount`
 * finite numbers. Rejects a wrong count, blanks and anything `Number()` does
 * not read as a number — a partially parsed geometry string is a wrong
 * geometry, not a recoverable one.
 */
function parseDecimalString(
    raw: string,
    expectedCount: number,
    field: string
): number[] {
    const parts = raw.split(DICOM_VALUE_SEPARATOR);
    if (parts.length !== expectedCount) {
        throw new ImageManifestParseError(
            `Image manifest ${field} must have ${expectedCount} values, got ${parts.length}`
        );
    }
    return parts.map((part) => {
        const trimmed = part.trim();
        const parsed = trimmed === '' ? Number.NaN : Number(trimmed);
        if (!Number.isFinite(parsed)) {
            throw new ImageManifestParseError(
                `Image manifest ${field} contains a value that is not a number`
            );
        }
        return parsed;
    });
}

/**
 * Direction cosines → the 3×3 matrix the domain and the grid builder use:
 * column 0 the row cosines, column 1 the column cosines, column 2 their cross
 * product. Byte-for-byte the same construction the DICOM parser performs, so a
 * manifest-derived grid and a parse-derived grid cannot drift apart.
 */
function toOrientationMatrix(cosines: number[]): mat3 {
    const rowCosines = vec3.fromValues(cosines[0], cosines[1], cosines[2]);
    const colCosines = vec3.fromValues(cosines[3], cosines[4], cosines[5]);
    const normal = vec3.create();
    vec3.cross(normal, rowCosines, colCosines);
    return mat3.fromValues(
        rowCosines[0],
        rowCosines[1],
        rowCosines[2],
        colCosines[0],
        colCosines[1],
        colCosines[2],
        normal[0],
        normal[1],
        normal[2]
    );
}

/**
 * Window/level from the manifest. Both halves or neither: a Window Center
 * without a Window Width (or the reverse) is a half-specified clinical value,
 * and filling in the other half would be exactly the silent defaulting this
 * module exists to prevent.
 */
function parseWindowLevel(dto: ImageDetailResponse): WindowLevel | undefined {
    const hasCenter =
        dto.windowCenter !== null && dto.windowCenter !== undefined;
    const hasWidth = dto.windowWidth !== null && dto.windowWidth !== undefined;
    if (!hasCenter && !hasWidth) {
        return undefined;
    }
    if (hasCenter !== hasWidth) {
        throw new ImageManifestParseError(
            'Image manifest declares only one of Window Center / Window Width'
        );
    }
    const center = requireNumber(dto.windowCenter, 'Window Center');
    const width = requireNumber(dto.windowWidth, 'Window Width');
    if (width <= 0) {
        throw new ImageManifestParseError(
            `Image manifest Window Width must be greater than 0, got ${width}`
        );
    }
    return new WindowLevel(width, center);
}

function parseOptionalNumber(
    value: number | null | undefined,
    field: string
): number | undefined {
    if (value === null || value === undefined) {
        return undefined;
    }
    return requireNumber(value, field);
}

function parseSlice(
    dto: ImageSliceResponse,
    index: number
): ImageSeriesManifestSlice {
    const position = parseDecimalString(
        requireString(
            dto.imagePositionPatientMm,
            `slice ${index} Image Position Patient`
        ),
        3,
        `slice ${index} Image Position Patient`
    );
    return {
        sopInstanceUid: requireString(
            dto.sopInstanceUid,
            `slice ${index} SOP Instance UID`
        ),
        instanceNumber: requireNumber(
            dto.instanceNumber,
            `slice ${index} Instance Number`
        ),
        sliceLocationMm: requireNumber(
            dto.sliceLocationMm,
            `slice ${index} Slice Location`
        ),
        imagePositionPatientMm: vec3.fromValues(
            position[0],
            position[1],
            position[2]
        ),
    };
}

/**
 * Convert one `GET /api/images/{id}` response into an
 * {@link ImageSeriesManifest}.
 *
 * @throws ImageManifestParseError if any geometry field is absent or malformed
 * @throws DomainValidationError if the parsed geometry violates a domain
 *   invariant (the manifest re-checks everything at its own boundary)
 */
export function toImageSeriesManifest(
    dto: ImageDetailResponse
): ImageSeriesManifest {
    if (dto.isValid3D === false) {
        throw new ImageManifestParseError(
            'Image manifest reports the series is not a valid 3D image'
        );
    }

    const modality = strToModality(requireString(dto.modality, 'a modality'));
    if (modality === Modality.UNKNOWN) {
        throw new ImageManifestParseError(
            'Image manifest declares an unrecognised modality'
        );
    }

    const pixelSpacing = parseDecimalString(
        requireString(dto.pixelSpacingMm, 'Pixel Spacing'),
        2,
        'Pixel Spacing'
    );
    const cosines = parseDecimalString(
        requireString(dto.imageOrientationPatient, 'Image Orientation Patient'),
        6,
        'Image Orientation Patient'
    );

    const slices = dto.slices;
    if (slices === null || slices === undefined || slices.length === 0) {
        throw new ImageManifestParseError('Image manifest lists no slices');
    }

    return new ImageSeriesManifest({
        studyInstanceUid: requireString(
            dto.studyInstanceUid,
            'a Study Instance UID'
        ),
        seriesInstanceUid: requireString(
            dto.seriesInstanceUid,
            'a Series Instance UID'
        ),
        frameOfReferenceUid: requireString(
            dto.frameOfReferenceUid,
            'a Frame of Reference UID'
        ),
        modality,
        rows: requireNumber(dto.rows, 'Rows'),
        columns: requireNumber(dto.columns, 'Columns'),
        pixelSpacingMm: vec2.fromValues(pixelSpacing[0], pixelSpacing[1]),
        imageOrientationPatient: toOrientationMatrix(cosines),
        sliceThicknessMm: requireNumber(
            dto.sliceThicknessMm,
            'Slice Thickness'
        ),
        acquisitionWindowLevel: parseWindowLevel(dto),
        rescaleSlope: parseOptionalNumber(dto.rescaleSlope, 'Rescale Slope'),
        rescaleIntercept: parseOptionalNumber(
            dto.rescaleIntercept,
            'Rescale Intercept'
        ),
        expectedSliceCount: parseOptionalNumber(
            dto.expectedSliceCount,
            'expected slice count'
        ),
        isReceiving: dto.isReceiving ?? false,
        slices: slices.map(parseSlice),
    });
}
